import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export function Card({
  children,
  className,
  as: Tag = 'section',
}: {
  children: ReactNode
  className?: string
  as?: 'section' | 'div' | 'article'
}) {
  return (
    <Tag
      className={cn(
        // Radio grande y **sin sombra**.
        //
        // La sombra era lo que delataba que esto es una página web: una nube
        // gris debajo de cada caja. En la dirección «Editorial Pop» las cajas se
        // separan por aire y por tinte, no por elevación, y el radio generoso es
        // lo que les da silueta de app. Queda solo el filete, que sobre papel
        // marfil es necesario porque la superficie es casi del mismo tono.
        'rounded-hero border border-line bg-surface p-4',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

export function SectionHeader({
  title,
  action,
  className,
}: {
  title: string
  action?: ReactNode
  className?: string
}) {
  return (
    <header className={cn('mb-3 flex items-center justify-between gap-3', className)}>
      {/* Tipografía display y peso 800, igual que el rótulo del inicio: los
          títulos de sección hablan con la misma voz en toda la app. Antes eran
          semibold de 15 px en texto normal, así que una sección se distinguía
          de un párrafo por el color y poco más. */}
      <h2 className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-ink">
        {title}
      </h2>
      {action}
    </header>
  )
}

/**
 * Enlace de acción de una cabecera de sección: «Ver todo», «Agregar».
 *
 * Existe porque el patrón obvio —un botón con solo texto de 13 px— deja un
 * blanco de unos 18 px de alto, menos de la mitad del mínimo cómodo de 44. El
 * relleno crece el área táctil y el margen negativo lo devuelve a su sitio, así
 * que el bloque se ve exactamente igual y el dedo acierta.
 *
 * Estaba repetido a mano en cinco pantallas, cada una con su medida. Aquí se
 * decide una vez.
 */
export function AccionSeccion({
  onClick,
  children,
  icon,
}: {
  onClick: () => void
  children: ReactNode
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-my-2.5 -mr-2 flex min-h-[44px] shrink-0 items-center gap-1 rounded-pill px-2',
        // Índigo: esto es un enlace, no un botón. Es justo la distinción que
        // sostiene la paleta —tinta para lo que se pulsa como botón, índigo para
        // el dato y los enlaces— y por la que los botones dejaron de ser índigo.
        'text-[13px] font-bold text-primary-text active:scale-95',
      )}
    >
      {icon}
      {children}
    </button>
  )
}

/**
 * Estado vacío.
 *
 * Una pantalla sin datos es una invitación a actuar, no un hueco: dice qué
 * falta y ofrece el siguiente paso, en vez de dejar al usuario adivinando si
 * algo se rompió.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      {icon ? <div className="text-ink-faint">{icon}</div> : null}
      <div className="flex flex-col gap-1">
        <p className="text-[15px] font-semibold text-ink">{title}</p>
        <p className="text-sm text-ink-soft">{description}</p>
      </div>
      {action}
    </div>
  )
}
